import { Box, Chip, Paper, Skeleton, Typography } from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { Link } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { DateTime } from 'luxon';
import { OfferSummary } from '../../interfaces/Offer/OfferSummary';
import { useThemeColor } from '../../hooks/useThemeColor';
import { sharedColors } from '../../utils/Style';
import { formatLocalTime } from '../../utils/Date';
import { useLocalizedText } from '../../hooks/useLocalizedText';

interface OfferCardProps {
  offer?: OfferSummary;
  applied?: boolean;
}

export const OfferCard = ({ offer, applied = false }: OfferCardProps) => {
  const t = useLocalizedText();

  const { primary } = useThemeColor();

  const showEndAt =
    !!offer?.endAt &&
    DateTime.fromJSDate(offer?.endAt).diffNow('days').days < 365;

  const content = (
    <Paper
      elevation={3}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        gap: 1.5,
        p: 1.5,
        boxSizing: 'border-box',
        width: '100%',
      }}
    >
      <LocalOfferIcon sx={{ color: primary }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 1.5,
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {offer ? (
              <>
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: sharedColors.gray6,
                  }}
                >
                  {offer.name}
                </Typography>

                {showEndAt && (
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: sharedColors.gray6,
                      fontWeight: 400,
                    }}
                  >
                    {t('offer.expiration_time', {
                      time: formatLocalTime(offer.endAt),
                    })}
                  </Typography>
                )}
              </>
            ) : (
              <>
                <Skeleton variant='rectangular' width={100} height={24} />
                <Skeleton
                  variant='rectangular'
                  width={80}
                  height={20}
                  sx={{ my: 0.5 }}
                />
              </>
            )}
          </Box>

          {applied && (
            <Chip
              size='small'
              icon={<CheckCircleOutlineIcon />}
              color='success'
              label={t('offer.applied')}
              sx={{ fontWeight: 600, width: 'fit-content', my: 0.5 }}
            />
          )}
        </Box>

        {offer ? (
          <Typography
            sx={{
              fontSize: '14px',
              color: sharedColors.gray4,
              fontWeight: 400,
            }}
          >
            {offer.description}
          </Typography>
        ) : (
          <Skeleton variant='rectangular' width='100%' height={100} />
        )}
      </Box>
    </Paper>
  );

  return offer && offer.endAt > new Date() ? (
    <Link
      to={`/kampanya/${offer.id}`}
      style={{ textDecoration: 'none', width: '100%' }}
    >
      {content}
    </Link>
  ) : (
    content
  );
};
